import { storeToRefs } from 'pinia';
import { useCartStore } from '../stores/cart';
import { AddCartItemsResult, CartItemQuantity } from '../types/models';
import { AddToCartItem, ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import { ShopifyCartRequestError, useShopifyCartService } from './shopifyCart';
import { enqueueCartMutation } from './cartMutationQueue';
import { getLineIdentity } from './lineItemProperties';
import { useCartValidation } from '../composables/useCartValidation';

const CART_RESYNC_DELAY = 2000;
const MAX_CART_RESYNC_ATTEMPTS = 3;
const STOCK_REJECTION_STATUS = 422;

type CartMutationResult = {
  error: unknown;
  cartBefore: ShopifyCart | null;
  fetchedCart: ShopifyCart | null;
};

let isCartStale = false;
let cartResyncAttempts = 0;
let cartResyncTimer: ReturnType<typeof setTimeout> | null = null;

const hasUntargetedReduction = (
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
  isTargeted: (item: ShopifyCartItem) => boolean,
): boolean =>
  cartBefore.items.some((item) => {
    if (isTargeted(item)) return false;
    const quantityAfter = cartAfter.items.find(({ key }) => key === item.key)?.quantity ?? 0;
    return quantityAfter < item.quantity;
  });

const getLineQuantities = (cart: ShopifyCart): Map<string, number> =>
  cart.items.reduce((quantities, item) => {
    const lineIdentity = getLineIdentity(item.variant_id, item.properties);
    return quantities.set(lineIdentity, (quantities.get(lineIdentity) ?? 0) + item.quantity);
  }, new Map<string, number>());

const getAddedQuantities = (
  items: AddToCartItem[],
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
): number[] => {
  const quantitiesBefore = getLineQuantities(cartBefore);
  const unclaimedQuantities = new Map(
    [...getLineQuantities(cartAfter)].map(([lineIdentity, quantity]) => [
      lineIdentity,
      Math.max(0, quantity - (quantitiesBefore.get(lineIdentity) ?? 0)),
    ]),
  );
  return items.map(({ id, properties, quantity }) => {
    const lineIdentity = getLineIdentity(id, properties);
    const unclaimedQuantity = unclaimedQuantities.get(lineIdentity) ?? 0;
    const addedQuantity = Math.min(unclaimedQuantity, quantity);
    unclaimedQuantities.set(lineIdentity, unclaimedQuantity - addedQuantity);
    return addedQuantity;
  });
};

export const cartActions = () => {
  const { DEFAULT_EMPTY_CART, updateCart, getCart, clearCart, addToCart } = useShopifyCartService();
  const cartStore = useCartStore();
  const { setCart, setCartAdjustedForStock, setCartAddLimitedByStock } = cartStore;
  const { cartData, cartDataLoaded } = storeToRefs(cartStore);
  const { validateCart } = useCartValidation();

  const scheduleCartResync = () => {
    if (cartResyncTimer || cartResyncAttempts >= MAX_CART_RESYNC_ATTEMPTS) return;
    cartResyncAttempts += 1;
    cartResyncTimer = setTimeout(() => {
      cartResyncTimer = null;
      refreshCart();
    }, CART_RESYNC_DELAY);
  };

  const syncCart = async (): Promise<ShopifyCart | null> => {
    try {
      const fetchedCart = await getCart();
      setCart(await validateCart(fetchedCart));
      isCartStale = false;
      cartResyncAttempts = 0;
      return fetchedCart;
    } catch (error) {
      console.error('Failed to sync cart', error);
      if (!cartDataLoaded.value) setCart(DEFAULT_EMPTY_CART());
      isCartStale = true;
      scheduleCartResync();
      return null;
    }
  };

  const runCartMutation = (
    mutate: () => Promise<unknown>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> =>
    enqueueCartMutation(async () => {
      if (!cartDataLoaded.value || isCartStale) await syncCart();
      const cartBefore = isCartStale ? null : cartData.value;
      const mutationError = await mutate().then(
        () => null,
        (error: unknown) => error,
      );
      const fetchedCart = await syncCart();
      const result = { error: mutationError, cartBefore, fetchedCart };
      if (!cartBefore || !fetchedCart) return result;
      if (hasUntargetedReduction(cartBefore, fetchedCart, isTargeted)) {
        setCartAdjustedForStock(true);
      }
      return result;
    });

  const refreshCart = (): Promise<ShopifyCart> =>
    enqueueCartMutation(async () => {
      await syncCart();
      return cartData.value;
    });

  const addCartItems = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    setCartAddLimitedByStock(false);
    const addedLineIdentities = new Set(
      items.map(({ id, properties }) => getLineIdentity(id, properties)),
    );
    const { error, cartBefore, fetchedCart } = await runCartMutation(
      () => addToCart(items),
      (item) => addedLineIdentities.has(getLineIdentity(item.variant_id, item.properties)),
    );
    if (!error) {
      return {
        outcome: 'complete',
        addedQuantities: items.map(({ quantity }) => quantity),
        isStockRejection: false,
      };
    }
    console.error('Failed to add items to cart', error);
    const isStockRejection =
      error instanceof ShopifyCartRequestError && error.status === STOCK_REJECTION_STATUS;
    if (!cartBefore || !fetchedCart) {
      return { outcome: 'unconfirmed', addedQuantities: items.map(() => 0), isStockRejection };
    }
    const addedQuantities = getAddedQuantities(items, cartBefore, fetchedCart);
    if (addedQuantities.every((quantity) => quantity === 0)) {
      return { outcome: 'rejected', addedQuantities, isStockRejection };
    }
    if (addedQuantities.every((quantity, index) => quantity >= items[index].quantity)) {
      return { outcome: 'complete', addedQuantities, isStockRejection: false };
    }
    if (isStockRejection) setCartAddLimitedByStock(true);
    return { outcome: 'partial', addedQuantities, isStockRejection };
  };

  const updateCartItemQuantities = async (items: CartItemQuantity[]): Promise<ShopifyCart> => {
    const updatedKeys = new Set(items.map(({ key }) => key));
    const { error } = await runCartMutation(
      async () => {
        const updates: Record<string, number> = items.reduce((updateObject, { key, quantity }) => {
          const currentItem = cartData.value.items.find((item) => key === item.key);
          if (!currentItem) {
            throw new Error(`Item with key ${key} doesn't exist in cart`);
          }
          updateObject[key] = quantity;
          return updateObject;
        }, {});
        await updateCart(updates);
      },
      (item) => updatedKeys.has(item.key),
    );
    if (error) console.error(error);
    return cartData.value;
  };

  const removeCartItems = async (items: string[]): Promise<ShopifyCart> => {
    const formattedItems: CartItemQuantity[] = items.map((key) => ({ key, quantity: 0 }));
    return updateCartItemQuantities(formattedItems);
  };

  const emptyCart = async (): Promise<ShopifyCart> => {
    const { error } = await runCartMutation(clearCart, () => true);
    if (error) console.error(error);
    return cartData.value;
  };

  return {
    refreshCart,
    addCartItems,
    updateCartItemQuantities,
    removeCartItems,
    emptyCart,
  };
};
